import BaseService from '../../common/BaseService';
import ACClient from '../../clients/ACClient';
import acTransformer from './acTransformer';

const AC_CRUD_METHODS = {
  GET: 'get',
  POST: 'create',
  DELETE: 'delete',
  PUT: 'update',
  PATCH: 'patch',
};

class ACService extends BaseService {
  constructor() {
    super({ name: 'ACService' });
    this.workatoSiteSettingsClient = null;
  }

  async setUserSiteSetting({
    userId, key, value,
  }) {
    if (!this.userSiteSettingsClient) {
      this.userSiteSettingsClient = createUserSiteSettingsClient();
    }

    const requestSettings = acTransformer.transformSetUserSiteSettingRequest({ userId, value });

    let setUserSiteSettingResponse;
    try {
      setUserSiteSettingResponse = await this.sendClientRequest({
        client: this.userSiteSettingsClient,
        settings: requestSettings,
        errorMsg: `Can't update ${key} in site settings`,
      });
      setUserSiteSettingResponse = acTransformer.transformSetUserSiteSettingResponse(setUserSiteSettingResponse);
    } catch (error) {
      setUserSiteSettingResponse = Promise.reject(error);
      this.logError(`Error setting site setting ${key} for user ${userId}`);
    }
    return setUserSiteSettingResponse;
  }

  async getWorkatoSiteSettings() {
    const workatoSiteSettings = {
      method: AC_CRUD_METHODS.GET,
    };
    if (!this.workatoSiteSettingsClient) {
      this.workatoSiteSettingsClient = createWorkatoSiteSettingsClient();
    }

    let workatoSiteSettingsResponse;
    try {
      workatoSiteSettingsResponse = await this.sendClientRequest({
        client: this.workatoSiteSettingsClient,
        settings: workatoSiteSettings,
        errorMsg: 'Couldn\'t fetch workatoSiteSettings',
      });
      workatoSiteSettingsResponse = acTransformer.transformWorkatoSiteSettingsResponse(workatoSiteSettingsResponse);
    } catch (error) {
      workatoSiteSettingsResponse = Promise.reject(error);
      this.logError(`Error fetching voiceSiteSettings: ${error}`);
    }
    return workatoSiteSettingsResponse;
  }

  async getSkillsSettings() {
    const skillsSettings = {
      method: AC_CRUD_METHODS.GET,
    };
    if (!this.skillsClient) {
      this.skillsClient = createSkillsClient();
    }

    let skillsResponse;
    try {
      skillsResponse = await this.sendClientRequest({
        client: this.skillsClient,
        settings: skillsSettings,
        errorMsg: 'Couldn\'t fetch skillsSettings',
      });
      skillsResponse = acTransformer.transformSkillsResponse(skillsResponse);
    } catch (error) {
      skillsResponse = Promise.reject(error);
      this.logError(`Error fetching skillsSettings: ${error}`);
    }
    return skillsResponse;
  }

  async getAllWidgets() {
    const widgetSettings = {
      method: AC_CRUD_METHODS.GET,
    };

    let widgetsResponse;
    try {
      if (!this.widgetsGetClient) {
        this.widgetsGetClient = createWidgetsGetClient();
      }
      widgetsResponse = await this.sendClientRequest({
        client: this.widgetsGetClient,
        settings: widgetSettings,
        errorMsg: 'Couldn\'t fetch widgets',
      });
      widgetsResponse = acTransformer.transformWidgetsResponse(widgetsResponse);
    } catch (error) {
      widgetsResponse = Promise.reject(error);
      this.logError(`Error fetching widgets: ${error}`);
    }
    return widgetsResponse;
  }

  async createWidget(settings) {
    const widgetSettings = acTransformer.transformSetWidgetSettings(settings);

    let widgetsResponse;
    try {
      if (!this.widgetSetupClient) {
        this.widgetSetupClient = createWidgetSetupClient();
      }
      widgetsResponse = await this.sendClientRequest({
        client: this.widgetSetupClient,
        settings: widgetSettings,
        errorMsg: 'Couldn\'t create widget',
      });
    } catch (error) {
      widgetsResponse = Promise.reject(error);
      this.logError(`Error creating widget: ${error}`);
    }
    return widgetsResponse;
  }

  async updateWidget(settings) {
    const widgetSettings = acTransformer.transformUpdateWidgetSettings(settings);

    let widgetsResponse;
    try {
      if (!this.widgetUpdateClient) {
        this.widgetUpdateClient = createWidgetUpdateClient();
      }

      widgetsResponse = await this.sendClientRequest({
        client: this.widgetUpdateClient,
        settings: widgetSettings,
        errorMsg: 'Couldn\'t update widget',
      });
    } catch (error) {
      widgetsResponse = Promise.reject(error);
      this.logError(`Error updating widget: ${error}`);
    }
    return widgetsResponse;
  }

  async getMedalliaSiteSettings() {
    const medalliaSiteSettings = {
      method: AC_CRUD_METHODS.GET,
    };
    if (!this.workatoSiteSettingsClient) {
      this.workatoSiteSettingsClient = createMedalliaSiteSettingsClient();
    }

    let workatoSiteSettingsResponse;
    try {
      workatoSiteSettingsResponse = await this.sendClientRequest({
        client: this.workatoSiteSettingsClient,
        settings: medalliaSiteSettings,
        errorMsg: 'Couldn\'t fetch medalliaSiteSettings',
      });
      workatoSiteSettingsResponse = acTransformer.transformMedalliaSiteSettingsResponse(workatoSiteSettingsResponse);
    } catch (error) {
      workatoSiteSettingsResponse = Promise.reject(error);
      this.logError(`Error fetching voiceSiteSettings: ${error}`);
    }
    return workatoSiteSettingsResponse;
  }

  async getUserDetails({ userId, includeDeleted }) {
    const userDetailsResponseRequest = acTransformer.transformUsersRequest({ userId, includeDeleted });
    if (!this.userDetailsClient) {
      this.userDetailsClient = createUsersClient();
    }

    let userDetailsResponse;
    try {
      userDetailsResponse = await this.sendClientRequest({
        client: this.userDetailsClient,
        settings: userDetailsResponseRequest,
        errorMsg: 'Couldn\'t fetch userDetails',
      });
      userDetailsResponse = acTransformer.transformUserDetailsResponse(userDetailsResponse);
    } catch (error) {
      userDetailsResponse = Promise.reject(error);
      this.logError(`Error fetching userDetails: ${error}`);
    }
    return userDetailsResponse;
  }
}

function createUsersClient() {
  return new ACClient({
    resource: ACClient.RESOURCES.USERS,
    contextName: 'GetUserDetails',
  });
}

function createUserSiteSettingsClient() {
  return new ACClient({
    resource: ACClient.RESOURCES.USER_SITE_SETTINGS,
    contextName: 'SetUserSiteSetting',
  });
}

function createWorkatoSiteSettingsClient() {
  return new ACClient({
    resource: ACClient.RESOURCES.WORKATO_SITE_SETTINGS,
    contextName: 'GetWorkatoSiteSetting',
  });
}

function createSkillsClient() {
  return new ACClient({
    resource: ACClient.RESOURCES.SKILLS,
    contextName: 'GetSkillsSetting',
  });
}

function createMedalliaSiteSettingsClient() {
  return new ACClient({
    resource: ACClient.RESOURCES.MEDALLIA_SITE_SETTINGS,
    contextName: 'GetMedalliaSiteSetting',
  });
}

function createWidgetsGetClient() {
  return new ACClient({
    resource: ACClient.RESOURCES.WIDGETS,
    contextName: 'GetWidgets',
  });
}

function createWidgetSetupClient() {
  return new ACClient({
    resource: ACClient.RESOURCES.WIDGETS,
    contextName: 'SetWidget',
  });
}

function createWidgetUpdateClient() {
  const client = new ACClient({
    resource: ACClient.RESOURCES.WIDGETS,
    contextName: 'UpdateWidget',
  });
  return client;
}

const acService = new ACService();

export default acService;
